import {type FunctionalComponent, h} from '@stencil/core';
import {commStatusForWMS} from '../../../../../result-cards/utils/commStatusForWMS.';
import {formatDate} from '../../../../../result-cards/utils/DateFormatter';
import type { labelProviderInterface } from '../../../../../../LabelProviderInterface';
import {getLocaleCode} from '../../../../../../../helpers/locale';
import {getStatus} from '../../helpers/helpers';


export interface ProductCardCommStatus {
    displayAdditionalData?: boolean;
    commStatus: string
    labelProvider: labelProviderInterface;
    isAlternativeDivider?: boolean;
    isGridView?: boolean;
    isDashboardView?: boolean;
    commercializationDate?: string;
    endOfCommercializationDate?: string;
    endOfServiceDate?: string;
    rtl?: boolean;
    isCompare?: boolean;
    isApc?: boolean;
    language?: string;
    country?: string;
    importance?: string;
}

export const ProductCardCommStatus: FunctionalComponent<ProductCardCommStatus> & { commStatusNumber?: number } = (props) => {
    function getLabel(commStatus:string):string {
        const type = commStatusForWMS[commStatus]?.type;
        const fallbackLabel = commStatusForWMS[commStatus]?.label;
        return props.labelProvider(
          `result.ribbon.${type}`,
          fallbackLabel
        );
    }

    function getFirstLabel(commStatus:string):string {
        const type = commStatusForWMS[commStatus]?.firstType;
        const fallbackLabel = commStatusForWMS[commStatus]?.firstLabel;
        return props.labelProvider(
          `result.ribbon.${type}`,
          fallbackLabel
        );
    }

    function getSecondLabel(commStatus:string):string {
        const type = commStatusForWMS[commStatus]?.secondType;
        const fallbackLabel = commStatusForWMS[commStatus]?.secondLabel;
        return props.labelProvider(
          `result.ribbon.${type}`,
          fallbackLabel
        );
    }

    const tooltipLabels = {
      serviceAdditionalHeaderLabelKey: 'result.status.service-end.question',
      serviceAdditionalHeaderLabelFallback: 'What does the end of standard service mean?',

      serviceAdditionalInfoLabelKey: 'result.status.service-end.info',
      serviceAdditionalInfoLabelFallback: 'End of standard service is the last date Schneider Electric will be able to provide you maintenance services (repair, spare parts, etc.).',

      commercializationAdditionalHeaderLabelKey: 'result.status.commercialization-end.question',
      commercializationAdditionalHeaderLabelFallback: 'What does the end of commercialization mean?',

      commercializationAdditionalInfoLabelKey: 'result.status.commercialization-end.info',
      commercializationAdditionalInfoLabelFallback: 'End of commercialization is the last date Schneider Electric will accept an offer for this reference. However, after Sales Services (repair, spare parts, etc.) will continue until its end of life.',
    };

    function getColor(commStatus:string, isAdditional:boolean = false) {
        switch (commStatus) {
        case '14':
        case '05':
          return 'orange';
        case '16':
          if(isAdditional) return 'red';
          return 'orange';
        case '03':
        case '18':
        case '19':
          return 'red';
        default:
          return 'green';
        }
    }

    function getAdditionalData(commStatus:string, localeCode: string = null) {
        if(!props.displayAdditionalData) return null;
        switch (commStatus) {
        case '16':
          return {
            header: props.labelProvider(
              tooltipLabels.serviceAdditionalHeaderLabelKey,
              tooltipLabels.serviceAdditionalHeaderLabelFallback),
            info: props.labelProvider(
              tooltipLabels.serviceAdditionalInfoLabelKey,
              tooltipLabels.serviceAdditionalInfoLabelFallback),
            date: props.endOfServiceDate ? `${props.labelProvider('products.dropdown.on-date', '')} ${formatDate(props.endOfServiceDate, localeCode)}` : '',
            secondHeader: props.labelProvider(
              tooltipLabels.commercializationAdditionalHeaderLabelKey,
              tooltipLabels.commercializationAdditionalHeaderLabelFallback),
            secondInfo: props.labelProvider(
              tooltipLabels.commercializationAdditionalInfoLabelKey,
              tooltipLabels.commercializationAdditionalInfoLabelFallback),
            secondDate: props.endOfCommercializationDate ? `${props.labelProvider('products.dropdown.on-date', '')} ${formatDate(props.endOfCommercializationDate, localeCode)}` : '',
          };
        case '14':
        case '18':
        case '19':
          return {
            header: props.labelProvider(
              tooltipLabels.commercializationAdditionalHeaderLabelKey,
              tooltipLabels.commercializationAdditionalHeaderLabelFallback),
            info: props.labelProvider(
              tooltipLabels.commercializationAdditionalInfoLabelKey,
              tooltipLabels.commercializationAdditionalInfoLabelFallback),
            date: props.endOfCommercializationDate ? `${props.labelProvider('products.dropdown.on-date', '')} ${formatDate(props.endOfCommercializationDate, localeCode)}` : '',
          };
        case '06':
          return {
            date: props.commercializationDate ? `${props.labelProvider('products.dropdown.on-date', '')} ${formatDate(props.commercializationDate, localeCode)}` : '',
          };
        default:
          return null;
        }
    }
    const localeCode = getLocaleCode({language: props.language, country: props.country});
    const additionalData = getAdditionalData(props.commStatus, localeCode);
    const displaySecondStatus = !!(commStatusForWMS[props.commStatus]?.secondType && props.endOfCommercializationDate);
    const displayOtherLabel = commStatusForWMS[props.commStatus]?.firstType;

    ProductCardCommStatus.commStatusNumber = displaySecondStatus ? 2 : (commStatusForWMS[props.commStatus] ? 1 : 0);

    return commStatusForWMS[props.commStatus]
      ? (<div class={{'product-card__comm-status--multiple': displaySecondStatus && !props.isGridView}}>
        <section class='product-card__comm-status-section'>
          <svs-status-v2
            label={displayOtherLabel ? getFirstLabel(props.commStatus) : getLabel(props.commStatus)}
            color={getColor(props.commStatus)}
            status={getStatus(props.commStatus)}
            dateLabel={additionalData?.date}
            additionalHeader={additionalData?.header}
            additionalInfo={additionalData?.info}
            hideTooltipForMobile={false}
            rtl={props.rtl}
            isGridView={props.isGridView}
            isDashboardView={props.isDashboardView}
            importance={props.importance}
          />
        </section>
        {displaySecondStatus ? <section class='product-card__comm-status-section'>
          <svs-status-v2
            label={getSecondLabel(props.commStatus)}
            color={getColor(props.commStatus, true)}
            status={getStatus(props.commStatus, true)}
            dateLabel={additionalData?.secondDate}
            additionalHeader={additionalData?.secondHeader}
            additionalInfo={additionalData?.secondInfo}
            hideTooltipForMobile={false}
            rtl={props.rtl}
            isGridView={props.isGridView}
            isDashboardView={props.isDashboardView}
            importance={props.importance}
          />
        </section> : ''}
      </div>
      )
      : '';
};
